import { collectBrowserDetails, type BrowserDetailsInput } from 'services/zoomDiagnostics/collectBrowserDetails'

export type DeviceClass = 'desktop' | 'mobile' | 'tablet'

export interface DeviceDetailsInput extends BrowserDetailsInput {
    screenWidth?: number
    screenHeight?: number
    devicePixelRatio?: number
    viewportWidth?: number
    viewportHeight?: number
    touchEnabled?: boolean
}

export interface DeviceDetailsSnapshot {
    userAgent: string
    platform: string
    language: string
    hardwareConcurrency: number
    deviceMemory?: number
    maxRenderableVideos?: number
    isSupportedBrowser: boolean
    isAboveMinRequirement: boolean
    browserEffectiveType?: string
    browserDownlink?: number
    browserRtt?: number
    deviceClass: DeviceClass
    screenWidth: number
    screenHeight: number
    devicePixelRatio: number
    viewportWidth: number
    viewportHeight: number
    touchEnabled: boolean
}

const MOBILE_UA_PATTERN = /iPhone|iPod|Android.*Mobile|Windows Phone/i
const TABLET_UA_PATTERN = /iPad|Android(?!.*Mobile)|Tablet/i

const MOBILE_VIEWPORT_MAX = 768
const TABLET_VIEWPORT_MAX = 1024

export const deriveDeviceClass = (
    userAgent: string,
    touchEnabled: boolean,
    viewportWidth: number
): DeviceClass => {
    if (MOBILE_UA_PATTERN.test(userAgent)) {
        return 'mobile'
    }

    if (TABLET_UA_PATTERN.test(userAgent)) {
        return 'tablet'
    }

    if (touchEnabled) {
        if (viewportWidth <= MOBILE_VIEWPORT_MAX) {
            return 'mobile'
        }

        if (viewportWidth <= TABLET_VIEWPORT_MAX) {
            return 'tablet'
        }
    }

    return 'desktop'
}

export const collectDeviceDetails = (input: DeviceDetailsInput = {}): DeviceDetailsSnapshot => {
    const browser = collectBrowserDetails(input)

    const screenWidth = input.screenWidth ?? (typeof screen !== 'undefined' ? screen.width : 0)
    const screenHeight = input.screenHeight ?? (typeof screen !== 'undefined' ? screen.height : 0)
    const devicePixelRatio = input.devicePixelRatio ?? (typeof window !== 'undefined' ? window.devicePixelRatio : 1)
    const viewportWidth = input.viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0)
    const viewportHeight = input.viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 0)
    const touchEnabled = input.touchEnabled ?? (typeof window !== 'undefined' && 'ontouchstart' in window)

    const deviceClass = deriveDeviceClass(browser.userAgent, touchEnabled, viewportWidth)

    return {
        ...browser,
        deviceClass,
        screenWidth,
        screenHeight,
        devicePixelRatio,
        viewportWidth,
        viewportHeight,
        touchEnabled
    }
}

export const toSnapshotDeviceFaroAttributes = (snapshot: DeviceDetailsSnapshot): Record<string, string> => {
    const attributes: Record<string, string> = {
        viewportWidth: String(snapshot.viewportWidth),
        viewportHeight: String(snapshot.viewportHeight)
    }

    if (snapshot.browserEffectiveType) {
        attributes.browserEffectiveType = snapshot.browserEffectiveType
    }

    if (snapshot.browserDownlink !== undefined) {
        attributes.browserDownlink = String(snapshot.browserDownlink)
    }

    if (snapshot.browserRtt !== undefined) {
        attributes.browserRtt = String(snapshot.browserRtt)
    }

    return attributes
}

export const toSessionCapabilityFaroAttributes = (snapshot: DeviceDetailsSnapshot): Record<string, string> => {
    const attributes: Record<string, string> = {
        deviceClass: snapshot.deviceClass,
        isAboveMinRequirement: String(snapshot.isAboveMinRequirement)
    }

    if (snapshot.maxRenderableVideos !== undefined) {
        attributes.maxRenderableVideos = String(snapshot.maxRenderableVideos)
    }

    return attributes
}

export const toFaroAttributes = (snapshot: DeviceDetailsSnapshot): Record<string, string> => {
    const attributes: Record<string, string> = {
        userAgent: snapshot.userAgent,
        platform: snapshot.platform,
        language: snapshot.language,
        hardwareConcurrency: String(snapshot.hardwareConcurrency),
        isSupportedBrowser: String(snapshot.isSupportedBrowser),
        isAboveMinRequirement: String(snapshot.isAboveMinRequirement),
        deviceClass: snapshot.deviceClass,
        screenWidth: String(snapshot.screenWidth),
        screenHeight: String(snapshot.screenHeight),
        devicePixelRatio: String(snapshot.devicePixelRatio),
        viewportWidth: String(snapshot.viewportWidth),
        viewportHeight: String(snapshot.viewportHeight),
        touchEnabled: String(snapshot.touchEnabled)
    }

    if (snapshot.deviceMemory !== undefined) {
        attributes.deviceMemory = String(snapshot.deviceMemory)
    }

    if (snapshot.maxRenderableVideos !== undefined) {
        attributes.maxRenderableVideos = String(snapshot.maxRenderableVideos)
    }

    if (snapshot.browserEffectiveType) {
        attributes.browserEffectiveType = snapshot.browserEffectiveType
    }

    if (snapshot.browserDownlink !== undefined) {
        attributes.browserDownlink = String(snapshot.browserDownlink)
    }

    if (snapshot.browserRtt !== undefined) {
        attributes.browserRtt = String(snapshot.browserRtt)
    }

    return attributes
}
